/**
 * The tint colours derived from a tenant's primary brand colour.
 *
 * Why this exists: `applyCssVars` used to push only --brand-primary,
 * --brand-primary-dark and --brand-accent. The tint variables
 * (--brand-primary-light, --brand-active-bg, --brand-active-text) stayed at
 * whatever index.css declared, so a tenant who set a purple primary got purple
 * buttons and a GREEN active-nav tint and green avatar circles. Deriving them
 * closes that gap.
 *
 * Why not CSS `color-mix(in oklab, var(--brand-primary) N%, white)`, which would
 * need no JS and would track the variable automatically: color-mix interpolates
 * every channel together, so reaching the design canvas's tint chroma (C≈0.03)
 * requires roughly a 37% mix, which also drags lightness down to L≈0.78 — far too
 * dark for a background wash. The canvas tint is not a mix of the primary and
 * white; it is a designed colour holding lightness HIGH and chroma MODERATE
 * independently. Only converting to OKLCH and setting the two separately
 * reproduces it.
 */

// Target lightness/chroma for a tint, taken from the design canvas's
// oklch(0.95 0.03 155) beside its oklch(0.40 0.08 155) primary.
const TINT_L = 0.95;
const TINT_C = 0.03;

// Where the search for a readable on-wash text colour starts, and how far down
// it may go. Settings validates a colour against HEX_RE and nothing else, so
// #fff is a value a tenant can actually save, and the raw primary as on-wash
// text made every tinted control and avatar initial invisible.
//
// A LIGHTNESS TARGET IS NOT A CONTRAST GUARANTEE. A saturated hue at L=0.50 is
// outside the sRGB gamut at its own chroma, so oklchToHex clips the channels and
// hands back a colour lighter than that L implies — #00FF00 measured 4.26:1 and
// #00FFFF 4.43:1 while a fixed-L clamp reported success. So the loop below
// measures the ACTUAL sRGB result and keeps darkening until it clears the ratio.
const TEXT_L_START = 0.5;
const TEXT_L_FLOOR = 0.1;
const TEXT_L_STEP = 0.02;
const TEXT_CONTRAST_MIN = 4.5;

// Ink, for the pathological case where even L=TEXT_L_FLOOR at this hue cannot
// reach the ratio. Never reached by any sRGB input in the tests; present so the
// function cannot return an unreadable colour by falling out of the loop.
const INK = '#1F2933';

/** WCAG 2.1 relative luminance of an sRGB colour. */
function relativeLuminance([r, g, b]: [number, number, number]): number {
  const channel = (v: number): number => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/**
 * WCAG 2.1 contrast ratio between two hex colours, or 0 if either is unreadable.
 *
 * Deliberately NOT exported: brandTints.test.ts carries its own copy so the
 * assertion is an independent measurement rather than a restatement of this
 * function. A test that shares the implementation it checks proves only that
 * the code agrees with itself.
 */
function contrast(aHex: string, bHex: string): number {
  const a = parseHex(aHex);
  const b = parseHex(bHex);
  if (!a || !b) return 0;
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x) as [
    number,
    number,
  ];
  return (hi + 0.05) / (lo + 0.05);
}

type Oklch = { L: number; C: number; h: number };

function srgbToLinear(v: number): number {
  const c = v / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function linearToSrgb(u: number): number {
  const c = u <= 0.0031308 ? 12.92 * u : 1.055 * Math.pow(Math.max(u, 0), 1 / 2.4) - 0.055;
  return Math.max(0, Math.min(255, Math.round(c * 255)));
}

/** Parses #rgb or #rrggbb. Returns null for anything else — callers fall back. */
export function parseHex(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let body = m[1]!;
  if (body.length === 3) {
    body = body
      .split('')
      .map((ch) => ch + ch)
      .join('');
  }
  return [
    parseInt(body.slice(0, 2), 16),
    parseInt(body.slice(2, 4), 16),
    parseInt(body.slice(4, 6), 16),
  ];
}

function toHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map((v) => v.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()
  );
}

/**
 * A CSS-safe `#RRGGBB`, or null for anything that is not a colour.
 *
 * Takes `unknown` deliberately. Branding reaches the client as a cast of raw
 * JSONB, so a field the TypeScript type calls `string` can be null, a number or
 * an object at runtime — and `parseHex` calls `.trim()`, so handing it a null
 * THROWS. In BrandingProvider that throw is caught by a handler that replaces
 * the tenant's ENTIRE branding with the product fallback, so one malformed
 * colour would discard their company name and logo as well.
 *
 * It also normalises, which validation alone does not: `parseHex`'s optional
 * `#` accepts a hash-less `fff`, and `--brand-primary: fff` is an invalid
 * declaration — every control painted with it loses its fill rather than
 * degrading to the product colour.
 */
export function normaliseHex(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const rgb = parseHex(value);
  return rgb ? toHex(rgb[0], rgb[1], rgb[2]) : null;
}

export function hexToOklch(hex: string): Oklch | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map(srgbToLinear) as [number, number, number];

  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

  const C = Math.sqrt(A * A + B * B);
  // Hue is meaningless at zero chroma; 0 keeps it deterministic.
  const h = C === 0 ? 0 : ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360;
  return { L, C, h };
}

export function oklchToHex({ L, C, h }: Oklch): string {
  const rad = (h * Math.PI) / 180;
  const A = C * Math.cos(rad);
  const B = C * Math.sin(rad);

  const l_ = L + 0.3963377774 * A + 0.2158037573 * B;
  const m_ = L - 0.1055613458 * A - 0.0638541728 * B;
  const s_ = L - 0.0894841775 * A - 1.291485548 * B;

  const l = l_ ** 3;
  const m = m_ ** 3;
  const s = s_ ** 3;

  const r = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const b = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;

  return toHex(linearToSrgb(r), linearToSrgb(g), linearToSrgb(b));
}

export type BrandTints = {
  /** Background wash behind active nav rows, avatar circles and mobile CTAs. */
  light: string;
  /** Same wash; separate name because --brand-active-bg is its own variable. */
  activeBg: string;
  /** Text/icon colour on top of that wash. */
  activeText: string;
};

/**
 * Tints for a primary colour.
 *
 * Chroma is CLAMPED, never raised: a tenant whose brand is grey or near-grey
 * must not be handed a coloured tint it never asked for. Lightness is set
 * outright, because that is what makes a wash a wash.
 *
 * `activeText` is the primary itself rather than a darkened variant — it sits on
 * the light wash, where the primary already carries strong contrast, and using
 * it keeps the active row visibly the same colour as the buttons.
 *
 * An unparseable colour returns null so the caller can keep its own defaults;
 * inventing a tint from a value we could not read would be worse than leaving
 * the stylesheet's.
 */
export function brandTints(primaryHex: string): BrandTints | null {
  const rgb = parseHex(primaryHex);
  const oklch = hexToOklch(primaryHex);
  if (!rgb || !oklch) return null;
  const wash = oklchToHex({ L: TINT_L, C: Math.min(TINT_C, oklch.C), h: oklch.h });
  // Normalised rather than echoed back: `#abc` and `#AABBCC` are the same colour,
  // and these values are written into CSS variables that get compared and logged.
  return { light: wash, activeBg: wash, activeText: onWashText(oklch, rgb, wash) };
}

/**
 * A version of the brand colour that is readable on its own wash.
 *
 * The tenant's colour VERBATIM whenever it already clears the ratio — which is
 * every brand dark enough to be used as text, so the common case keeps the
 * active row visibly the same colour as the buttons. Only a brand too pale to
 * read gets darkened, and it keeps its hue and chroma so it is still recognisably
 * their colour.
 */
function onWashText(oklch: Oklch, rgb: [number, number, number], wash: string): string {
  const raw = toHex(rgb[0], rgb[1], rgb[2]);
  if (contrast(wash, raw) >= TEXT_CONTRAST_MIN) return raw;

  for (let L = TEXT_L_START; L >= TEXT_L_FLOOR; L -= TEXT_L_STEP) {
    const candidate = oklchToHex({ L, C: oklch.C, h: oklch.h });
    if (contrast(wash, candidate) >= TEXT_CONTRAST_MIN) return candidate;
  }
  return INK;
}
